import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Navigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Trash2, Edit2, Plus, X, LogOut, ExternalLink, ChevronUp, ChevronDown, Star } from 'lucide-react';
import { useAuth, API } from '../context/AuthContext';

const EMPTY = { brand_name: '', celebrity_name: '', caption: '', instagram_url: '', image_url: '', featured: false };

export default function AdminDashboard() {
  const { admin, token, checking, logout } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState('campaigns');
  const [leads, setLeads] = useState([]);

  const authH = { headers: { Authorization: `Bearer ${token}` } };

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/campaigns`);
      setCampaigns(data);
    } catch (e) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data);
    } catch (e) { /* ignore */ }
  }, [token]);

  useEffect(() => {
    if (admin) { fetchCampaigns(); fetchLeads(); }
  }, [admin, fetchCampaigns, fetchLeads]);

  if (checking) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      brand_name: c.brand_name || '',
      celebrity_name: c.celebrity_name || '',
      caption: c.caption || '',
      instagram_url: c.instagram_url || '',
      image_url: c.image_url || '',
      featured: c.featured || false,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY);
    setShowForm(false);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.brand_name || !form.instagram_url) {
      toast.error('Brand name and Instagram URL are required.');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/admin/campaigns/${editingId}`, form, authH);
        toast.success('Campaign updated');
      } else {
        await axios.post(`${API}/admin/campaigns`, form, authH);
        toast.success('Campaign added');
      }
      resetForm();
      fetchCampaigns();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed.');
    }
  };

  const moveCampaign = async (id, direction) => {
    try {
      await axios.post(`${API}/admin/campaigns/${id}/move`, { direction }, authH);
      fetchCampaigns();
    } catch (e) { toast.error('Reorder failed'); }
  };

  const toggleFeatured = async (c) => {
    try {
      await axios.put(`${API}/admin/campaigns/${c.id}`, {
        brand_name: c.brand_name || '',
        celebrity_name: c.celebrity_name || '',
        caption: c.caption || '',
        instagram_url: c.instagram_url || '',
        image_url: c.image_url || '',
        featured: !c.featured,
      }, authH);
      toast.success(c.featured ? 'Removed from homepage' : 'Shown on homepage');
      fetchCampaigns();
    } catch (e) { toast.error('Update failed'); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await axios.delete(`${API}/admin/campaigns/${id}`, authH);
      toast.success('Deleted');
      fetchCampaigns();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="min-h-screen bg-[#f5f1e8]">
      <header className="border-b border-[#1a3a2e]/15 bg-[#f5f1e8]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1">
            <span className="font-serif text-2xl text-[#1a3a2e]">rg</span>
            <span className="font-serif text-2xl text-[#8b5a2b]">.</span>
            <span className="ml-3 text-xs tracking-[0.25em] uppercase text-[#3a5a4e]">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#3a5a4e] hidden sm:block">{admin.email}</span>
            <button
              onClick={() => { logout(); toast.success('Signed out'); }}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#1a3a2e] text-sm text-[#1a3a2e] hover:bg-[#1a3a2e] hover:text-[#f5f1e8] transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-4xl text-[#1a3a2e]">Dashboard</h1>
            <p className="text-sm text-[#3a5a4e] mt-1">Manage campaigns shown on the public site.</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setTab('campaigns')}
              data-testid="tab-campaigns"
              className={`px-4 py-2 text-sm ${tab === 'campaigns' ? 'bg-[#1a3a2e] text-[#f5f1e8]' : 'border border-[#1a3a2e]/30 text-[#1a3a2e]'}`}
            >Campaigns ({campaigns.length})</button>
            <button
              onClick={() => setTab('leads')}
              data-testid="tab-leads"
              className={`px-4 py-2 text-sm ${tab === 'leads' ? 'bg-[#1a3a2e] text-[#f5f1e8]' : 'border border-[#1a3a2e]/30 text-[#1a3a2e]'}`}
            >Enquiries ({leads.length})</button>
          </div>
        </div>

        {tab === 'campaigns' && (
          <>
            {/* Add / Edit form */}
            <div className="mt-8 border border-[#1a3a2e]/15 bg-white">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a3a2e]/10">
                <p className="font-serif text-xl text-[#1a3a2e]">
                  {editingId ? 'Edit campaign' : 'Add a new campaign'}
                </p>
                <button
                  onClick={() => (showForm ? resetForm() : setShowForm(true))}
                  data-testid="toggle-add-form"
                  className="inline-flex items-center gap-1.5 text-sm text-[#1a3a2e]"
                >
                  {showForm ? <><X size={14} /> Close</> : <><Plus size={14} /> New</>}
                </button>
              </div>
              {showForm && (
                <form onSubmit={submitForm} className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="campaign-form">
                  <AField label="Brand name *" name="brand_name" value={form.brand_name} setForm={setForm} testid="form-brand" />
                  <AField label="Celebrity / Talent" name="celebrity_name" value={form.celebrity_name} setForm={setForm} testid="form-celebrity" />
                  <AField label="Instagram URL *" name="instagram_url" value={form.instagram_url} setForm={setForm} testid="form-instagram" />
                  <AField label="Image URL (optional)" name="image_url" value={form.image_url} setForm={setForm} testid="form-image" />
                  <div className="md:col-span-2">
                    <label className="text-xs tracking-[0.25em] uppercase text-[#3a5a4e]">Caption</label>
                    <textarea
                      value={form.caption}
                      onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))}
                      rows={3}
                      data-testid="form-caption"
                      className="mt-2 block w-full border border-[#1a3a2e]/20 px-3 py-2 text-[#1a3a2e] focus:border-[#1a3a2e]"
                    />
                  </div>
                  <div className="md:col-span-2 flex gap-3">
                    <button
                      type="submit"
                      data-testid="form-submit"
                      className="px-6 py-3 bg-[#1a3a2e] text-[#f5f1e8] text-sm hover:bg-[#122a21]"
                    >{editingId ? 'Save changes' : 'Add campaign'}</button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-3 border border-[#1a3a2e]/30 text-sm text-[#1a3a2e]"
                    >Cancel</button>
                  </div>
                </form>
              )}
            </div>

            {/* List */}
            <div className="mt-10 border border-[#1a3a2e]/15 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-[#e3d9c6]/60 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium text-[#1a3a2e]">Brand</th>
                    <th className="px-4 py-3 font-medium text-[#1a3a2e]">Celebrity</th>
                    <th className="px-4 py-3 font-medium text-[#1a3a2e]">Instagram</th>
                    <th className="px-4 py-3 font-medium text-[#1a3a2e]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-[#3a5a4e]">Loading…</td></tr>
                  ) : campaigns.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-[#3a5a4e]">No campaigns yet. Add your first one above.</td></tr>
                  ) : campaigns.map((c, idx) => (
                    <tr key={c.id} className="border-t border-[#1a3a2e]/10" data-testid={`admin-row-${c.id}`}>
                      <td className="px-4 py-3 text-[#1a3a2e] font-medium">{c.brand_name}</td>
                      <td className="px-4 py-3 text-[#3a5a4e]">{c.celebrity_name || '—'}</td>
                      <td className="px-4 py-3">
                        <a href={c.instagram_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#1a3a2e] underline-anim truncate max-w-[280px]">
                          {c.instagram_url} <ExternalLink size={12} />
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 items-center">
                          <button onClick={() => toggleFeatured(c)} data-testid={`feature-${c.id}`} title={c.featured ? 'Remove from homepage top campaigns' : 'Show in homepage top campaigns'} className={`p-2 hover:bg-[#e3d9c6] ${c.featured ? 'text-[#8b5a2b]' : 'text-[#1a3a2e]/35'}`}><Star size={14} fill={c.featured ? 'currentColor' : 'none'} /></button>
                          <button onClick={() => moveCampaign(c.id, 'up')} disabled={idx === 0} data-testid={`move-up-${c.id}`} title="Move up" className="p-2 hover:bg-[#e3d9c6] disabled:opacity-30 disabled:hover:bg-transparent"><ChevronUp size={14} /></button>
                          <button onClick={() => moveCampaign(c.id, 'down')} disabled={idx === campaigns.length - 1} data-testid={`move-down-${c.id}`} title="Move down" className="p-2 hover:bg-[#e3d9c6] disabled:opacity-30 disabled:hover:bg-transparent"><ChevronDown size={14} /></button>
                          <button onClick={() => startEdit(c)} data-testid={`edit-${c.id}`} className="p-2 hover:bg-[#e3d9c6]"><Edit2 size={14} /></button>
                          <button onClick={() => deleteCampaign(c.id)} data-testid={`delete-${c.id}`} className="p-2 hover:bg-[#e3d9c6] text-red-700"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'leads' && (
          <div className="mt-8 border border-[#1a3a2e]/15 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#e3d9c6]/60 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium text-[#1a3a2e]">Name</th>
                  <th className="px-4 py-3 font-medium text-[#1a3a2e]">Email</th>
                  <th className="px-4 py-3 font-medium text-[#1a3a2e]">Message</th>
                  <th className="px-4 py-3 font-medium text-[#1a3a2e]">Received</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-[#3a5a4e]">No enquiries yet.</td></tr>
                ) : leads.map((l) => (
                  <tr key={l.id} className="border-t border-[#1a3a2e]/10">
                    <td className="px-4 py-3 text-[#1a3a2e]">{l.first_name} {l.last_name}</td>
                    <td className="px-4 py-3 text-[#3a5a4e]">{l.email}</td>
                    <td className="px-4 py-3 text-[#3a5a4e] max-w-md">{l.message}</td>
                    <td className="px-4 py-3 text-[#3a5a4e]">{new Date(l.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function AField({ label, name, value, setForm, testid }) {
  return (
    <div>
      <label className="text-xs tracking-[0.25em] uppercase text-[#3a5a4e]">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
        data-testid={testid}
        className="mt-2 block w-full border border-[#1a3a2e]/20 px-3 py-2 text-[#1a3a2e] focus:border-[#1a3a2e] outline-none"
      />
    </div>
  );
}
